import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { FaInfoCircle, FaSearch } from "react-icons/fa";
import { FaCopy } from "react-icons/fa6";
import { FiX } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setCeilingSelectedSimilarRoomWithIfcDesignNumber } from "../ceilingSlice";
import { CurrentRoomIfcDesignViewer } from "./CurrentRoomIfcDesignViewer";
import { SimilarRoomWithIfcDesignViewer } from "./SimilarRoomWithIfcDesignViewer";
import {
  selectSelectedSimilarRoomWithIfcDesign,
  selectSimilarRoomsWithIfcDesignCards,
  selectSimilarRoomsWithIfcDesignCurrentRoomName,
} from "./similarRoomsWithIfcDesignSelectors";
import { getPercentColor } from "../../../RoomColoring";
import {
  selectCopySimilarRoomWithIfcDesignToCurrentRoomState,
  selectCurrentRoomIfcDesignItemCount,
} from "./similarRoomsWithIfcDesignViewerSelectors";
import { thunkCopySimilarRoomWithIfcDesignToCurrentRoom } from "./thunkCopySimilarRoomWithIfcDesignToCurrentRoom";

const getSimilarityPercent = (score: number): number => Math.round(score * 100);
const formatSimilarity = (score: number): string => `${getSimilarityPercent(score)} %`;

type SimilarRoomsWithIfcDesignModalProps = {
  onClose: () => void;
};

export const SimilarRoomsWithIfcDesignModal: React.FC<SimilarRoomsWithIfcDesignModalProps> = ({
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState("");
  const cards = useAppSelector(selectSimilarRoomsWithIfcDesignCards);
  const roomName = useAppSelector(selectSimilarRoomsWithIfcDesignCurrentRoomName);
  const selectedRoom = useAppSelector(selectSelectedSimilarRoomWithIfcDesign);
  const copyState = useAppSelector(selectCopySimilarRoomWithIfcDesignToCurrentRoomState);
  const currentRoomIfcDesignItemCount = useAppSelector(selectCurrentRoomIfcDesignItemCount);
  const normalizedSearchText = searchText.trim().toLocaleLowerCase("nb-NO");
  const filteredCards = useMemo(() => {
    if (!normalizedSearchText) {
      return cards;
    }

    return cards.filter((card) =>
      [
        card.roomNumber,
        card.roomName,
        card.floorLabel,
        `${card.ifcDeviceCount} IFC`,
        `${card.similarityPercent} %`,
      ]
        .join(" ")
        .toLocaleLowerCase("nb-NO")
        .includes(normalizedSearchText)
    );
  }, [cards, normalizedSearchText]);

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Lignende rom med IFC-design${roomName ? `: ${roomName}` : ""}`}
      size="90vw"
      centered
    >
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.45fr) minmax(320px, 0.9fr)",
          gap: 16,
          height: "70vh",
          minHeight: 420,
        }}
      >
        <Paper withBorder radius="sm" p="md" style={{ minHeight: 0 }}>
          <Stack gap="sm" h="100%">
            <Group justify="space-between" align="center" gap="xs">
              <Text size="sm" fw={600}>
                Rom med belysning eller luftdon i IFC
              </Text>
              <Group gap="xs" wrap="nowrap">
                {selectedRoom && (
                  <Badge
                    variant="filled"
                    styles={{
                      root: {
                        backgroundColor: getPercentColor(
                          getSimilarityPercent(selectedRoom.similarityScore)
                        ),
                        color: "#ffffff",
                      },
                    }}
                  >
                    {formatSimilarity(selectedRoom.similarityScore)}
                  </Badge>
                )}
                {copyState.showCopyButton && (
                  <Button
                    size="xs"
                    variant="filled"
                    color="blue"
                    disabled={copyState.disabled}
                    leftSection={<FaCopy size={12} />}
                    onClick={() => dispatch(thunkCopySimilarRoomWithIfcDesignToCurrentRoom())}
                  >
                    Kopier IFC til aktuelt rom
                  </Button>
                )}
              </Group>
            </Group>
            <Text size="xs" c={copyState.disabled ? "dimmed" : "blue.7"}>
              {copyState.hint}
            </Text>

            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(210px, 280px) minmax(0, 1fr)",
                gap: 12,
                flex: 1,
                minHeight: 0,
              }}
            >
              <Stack gap="xs" style={{ minHeight: 0 }}>
                <TextInput
                  size="xs"
                  placeholder="Søk i rom"
                  value={searchText}
                  onChange={(event) => setSearchText(event.currentTarget.value)}
                  leftSection={<FaSearch size={11} />}
                  rightSection={
                    <Tooltip label="Tøm søk" withArrow>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        radius="xl"
                        disabled={!searchText.trim()}
                        onClick={() => setSearchText("")}
                      >
                        <FiX size={14} />
                      </ActionIcon>
                    </Tooltip>
                  }
                />
                <ScrollArea style={{ minHeight: 0 }}>
                  <Stack gap="xs" pr="xs">
                    {filteredCards.map((card) => (
                      <Paper
                        key={card.roomNumber}
                        withBorder
                        radius="sm"
                        p="sm"
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          dispatch(
                            setCeilingSelectedSimilarRoomWithIfcDesignNumber(card.roomNumber)
                          )
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            dispatch(
                              setCeilingSelectedSimilarRoomWithIfcDesignNumber(card.roomNumber)
                            );
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          borderColor: card.isSelected
                            ? card.similarityColor
                            : "var(--mantine-color-gray-3)",
                          backgroundColor: card.isSelected
                            ? `${card.similarityColor}14`
                            : "#ffffff",
                        }}
                      >
                        <Stack gap={6}>
                          <Group justify="space-between" gap="xs" wrap="nowrap">
                            <Box style={{ minWidth: 0 }}>
                              <Text size="sm" fw={600} truncate="end">
                                {card.roomNumber}
                              </Text>
                              <Text size="xs" c="dimmed" truncate="end">
                                {card.roomName}
                              </Text>
                            </Box>
                            <Badge
                              variant="filled"
                              styles={{
                                root: {
                                  backgroundColor: card.similarityColor,
                                  color: "#ffffff",
                                  flexShrink: 0,
                                },
                              }}
                            >
                              {card.similarityPercent} %
                            </Badge>
                          </Group>
                          <Group gap={6} wrap="wrap">
                            <Badge variant="light" color="gray" size="xs">
                              Etasje: {card.floorLabel}
                            </Badge>
                            <Badge variant="light" color="blue" size="xs">
                              {card.ifcDeviceCount} IFC
                            </Badge>
                          </Group>
                        </Stack>
                      </Paper>
                    ))}
                    {filteredCards.length === 0 && (
                      <Text size="xs" c="dimmed" ta="center" py="md">
                        Ingen rom matcher søket.
                      </Text>
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>

              <Box style={{ minHeight: 0 }}>
                <SimilarRoomWithIfcDesignViewer />
              </Box>
            </Box>
          </Stack>
        </Paper>

        <Paper withBorder radius="sm" p="md" style={{ minHeight: 0 }}>
          <Stack h="100%" gap="xs">
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                Aktuellt rom
              </Text>
              <Text size="xs" c="dimmed">
                {roomName}
              </Text>
            </Stack>
            {currentRoomIfcDesignItemCount > 0 && (
              <Alert
                variant="light"
                color="yellow"
                icon={<FaInfoCircle size={14} />}
                py={6}
              >
                <Text size="xs">
                  Aktuellt rom har {currentRoomIfcDesignItemCount} komponenter i IFC.
                </Text>
              </Alert>
            )}
            <Box style={{ flex: 1, minHeight: 0, width: "100%" }}>
              <CurrentRoomIfcDesignViewer />
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Modal>
  );
};
